import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AionzLightboxDirective, AionzTooltip, LightboxItem, LightboxItemType } from '@aionzdev/design-system';
import { PurchaseRequestAttachment } from '../../../interfaces/purchase-request-attachment';

@Component({
  selector: 'aionz-attachment-card',
  imports: [AionzLightboxDirective, DatePipe, MatButtonModule, MatIconModule, AionzTooltip],
  templateUrl: './attachment-card.component.html',
  styleUrl: './attachment-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttachmentCardComponent {
  public attachment = input.required<PurchaseRequestAttachment>();
  public download = output<PurchaseRequestAttachment>();

  protected readonly actionLabel = computed(() => `Visualizar ${this.attachment().fileName}`);
  protected readonly downloadLabel = computed(() => `Baixar ${this.attachment().fileName}`);

  protected readonly lightboxItems = computed<LightboxItem[]>(() => {
    const attachment = this.attachment();
    if (!attachment.url) return [];

    return [
      {
        src: attachment.url,
        alt: attachment.fileName,
        mimeType: attachment.fileType,
        type: this.resolveLightboxType(attachment.fileType)
      }
    ];
  });

  protected onDownloadClick(event: Event, attachment: PurchaseRequestAttachment): void {
  event.stopPropagation();

  if (this.isImageAttachment(attachment) && attachment.url) {
    void this.downloadFromUrl(attachment);
    return;
  }

  this.download.emit(attachment);
}

private async downloadFromUrl(attachment: PurchaseRequestAttachment): Promise<void> {
  try {
    const response = await fetch(attachment.url!);
    if (!response.ok) throw new Error(`Falha ao baixar arquivo: ${response.status}`);

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = attachment.fileName;
    link.click();

    URL.revokeObjectURL(blobUrl);
  } catch {
    this.download.emit(attachment);
  }
}

  private resolveLightboxType(fileType: string): LightboxItemType {
    const normalized = fileType.toLowerCase();
    if (normalized === 'pdf' || normalized === 'application/pdf') return 'pdf';
    if (normalized.startsWith('video/')) return 'video';
    return 'image';
  }

  protected isImageAttachment(attachment: PurchaseRequestAttachment): boolean {
    const type = attachment.fileType.toLowerCase();
    return type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(type);
  }
}